import { useTranslation } from "react-i18next"
import { useState, useMemo, useContext } from "react"
import { Route, Switch, useHistory, useParams } from "react-router-dom"
import { useQuery, useQueryClient } from "react-query"
import { UserContext } from "../../hooks/UserContext"
import { fetchProcessingOrders } from "../../services"
import { FormattedRelativeTime } from '../../components/FormattedRelativeTime'
import { ExclamationCircleIcon } from "@heroicons/react/outline"
import {
    NewTable,
    PageTopBar,
    PageView,
    SlidePanel,
} from "../../components"
import { Loader } from "../../components/Loader"
import { ShowOrderContainer } from "./ShowOrderContainer"
import { FULFILLMENT_ERRORS, FULFILLMENT_ERROR_ORDER_DETAIL } from "../../navigation/constants"
import { RESPONSIBLE_AREA_STYLES, CATEGORY_STYLES, ERROR_STATUS_STYLES } from "../../constants/fulfillmentErrorStyles"

const getResponsibleAreaFilterOptions = (i18n) => [
    { id: 'customer', label: i18n.t('fulfillment_errors.responsible_area.customer') },
    { id: 'cubbo', label: 'Cubbo (ops + lastmile + tech)' },
    { id: 'cubbo_ops', label: i18n.t('fulfillment_errors.responsible_area.cubbo_ops') },
    { id: 'lastmile', label: i18n.t('fulfillment_errors.responsible_area.lastmile') },
    { id: 'tech', label: i18n.t('fulfillment_errors.responsible_area.tech') },
]

const getErrorStatusFilterOptions = (i18n) => [
    { id: 'open', label: i18n.t('fulfillment_errors.error_status.open') },
    { id: 'pending_retry', label: i18n.t('fulfillment_errors.error_status.pending_retry') },
]

const getActiveError = (order) => {
    if (!Array.isArray(order.order_fulfillment_errors)) return null
    return order.order_fulfillment_errors.find(e => e.error_status === 'open' || e.error_status === 'pending_retry') || null
}

export const ListFulfillmentErrorsContainer = () => {
    const { i18n } = useTranslation()
    const history = useHistory()
    const { user } = useContext(UserContext)
    const { warehouse_id } = useParams()
    const queryClient = useQueryClient()
    // Starts enabled so the page loads its own data when opened. onSuccess flips it back
    // to false, which is how this screen throttles itself: one fetch per user action
    // (refresh, paginate, sort, filter). Starting at false made the very first render
    // that one action short — the operator arrived at an empty table and had to press
    // refresh to see errors that were already there.
    const [isFetchEnabled, setIsFetchEnabled] = useState(true)
    const [orders, setOrders] = useState([])

    const initialSearchParams = {
        warehouse_id: warehouse_id,
        page: 1,
        per_page: 25,
        has_fulfillment_error: true,
        // The one stuck the longest, on top. Sorts by the ERROR date and not by the
        // order's: an order from yesterday may have failed a minute ago, and what the queue
        // prioritises is the accumulated wait, which is the same thing the "Desde" column shows.
        order_by: 'fulfillment_error_created_at',
        order: 'asc',
    }
    const [searchParams, setSearchParams] = useState(initialSearchParams)

    const {
        isLoading,
        isError,
        error,
        data,
        isFetching,
        isPreviousData,
        refetch,
    } = useQuery(
        ['fulfillment_errors_orders', searchParams],
        () => fetchProcessingOrders(searchParams),
        {
            keepPreviousData: true,
            refetchOnWindowFocus: false,
            enabled: isFetchEnabled,
            onSuccess: (responseData) => {
                setIsFetchEnabled(false)
                if (responseData?.orders) setOrders(responseData.orders)
            },
            onError: () => {
                setIsFetchEnabled(false)
            }
        }
    )

    const onSortChange = (orderBy) => {
        if (orderBy.length > 0) {
            const newSearchParams = {
                ...searchParams,
                order_by: orderBy[0].id,
                order: orderBy[0].desc ? "desc" : "asc",
            }
            if (newSearchParams.order_by !== searchParams.order_by || newSearchParams.order !== searchParams.order) {
                setSearchParams(newSearchParams)
                if (orders?.length > 0) setIsFetchEnabled(true)
            }
        } else {
            // Clearing the sort goes back to the screen's own —oldest error on top—, not to the
            // backend's. The table also reports "no sort" on mount, and by deleting the keys
            // here the list was born sorted by ORDER date descending, exactly the opposite
            // of what the queue needs.
            const newSearchParams = {
                ...searchParams,
                order_by: initialSearchParams.order_by,
                order: initialSearchParams.order,
            }
            if (newSearchParams.order_by === searchParams.order_by && newSearchParams.order === searchParams.order) return
            setSearchParams(newSearchParams)
            if (orders?.length > 0) setIsFetchEnabled(true)
        }
    }

    const memoizedColumns = useMemo(() => {
        return [
            {
                Header: i18n.t('fulfillment_errors.page.headers.order_number'),
                accessor: "order_number",
            },
            {
                Header: i18n.t('fulfillment_errors.page.headers.store'),
                accessor: "store",
                disableSortBy: true,
            },
            {
                // The tote is what the operator has in front of them: with it they find the order on the
                // shelf. The error code and its message live in the row detail, which
                // is where they are read calmly; in the sweep view they only stole width.
                Header: i18n.t('fulfillment_errors.page.headers.tote'),
                accessor: "tote",
                disableSortBy: true,
            },
            {
                Header: i18n.t('fulfillment_errors.page.headers.category'),
                accessor: "category",
                disableSortBy: true,
            },
            {
                Header: i18n.t('fulfillment_errors.page.headers.responsible_area'),
                accessor: "responsible_area",
                disableSortBy: true,
            },
            {
                // disableWrap because NewTable puts `whitespace-nowrap` on every cell that does not
                // ask otherwise. These texts reach 65 characters ("Escalar a supervisor — la tienda
                // debe corregir el método de envío"), and without wrapping they spilled out of their own box
                // —the `max-w-xs` bounds the div, not the text— and overlapped the status
                // column, which is the next one. Wrapping onto two lines it reads in full, which is what
                // matters: this column says the only thing the operator can do.
                Header: i18n.t('fulfillment_errors.page.headers.operator_action'),
                accessor: "operator_action",
                disableSortBy: true,
                disableWrap: true,
            },
            {
                // The column that answers the question the operator comes here for: of everything
                // I left waiting, what can I retry now?
                Header: i18n.t('fulfillment_errors.page.headers.error_status'),
                accessor: "error_status",
                disableSortBy: true,
            },
            {
                // `id` goes to the backend, `accessor` pulls the value from the row: they differ because
                // the cell paints the error date but the sort has to be requested by the key
                // the backend understands. Without the id, a click here would sort by orders.created_at.
                Header: i18n.t('fulfillment_errors.page.headers.created_at'),
                id: "fulfillment_error_created_at",
                accessor: "created_at",
            },
        ]
    }, [i18n])

    const memoizedTableData = useMemo(() => {
        return orders.map((order) => {
            const activeError = getActiveError(order)
            const responsibleStyle = RESPONSIBLE_AREA_STYLES[activeError?.responsible_area] || 'bg-gray-100 text-gray-800'
            const categoryStyle = CATEGORY_STYLES[activeError?.category]?.pill || 'bg-gray-50 text-gray-700'

            return {
                object: order,
                order_number: (
                    <div className="font-normal text-base text-gray-700">
                        <span className="hover:underline cursor-pointer">#{String(order.order_number)}</span>
                    </div>
                ),
                store: (
                    <div className="text-sm text-gray-600">
                        {order.store?.name || '-'}
                    </div>
                ),
                // `totes` aggregates those of every task (multipicker); `tote` is the only one of the
                // classic picking order. All are shown because an order split between two
                // pickers lives in two totes and sending the operator to just one leaves it half done.
                tote: (
                    <div className="font-mono text-sm text-gray-700">
                        {(() => {
                            const codes = (order.picking_order?.totes?.length
                                ? order.picking_order.totes
                                : [order.picking_order?.tote].filter(Boolean)
                            ).map(t => t?.unique_code).filter(Boolean)
                            return codes.length ? codes.join(', ') : '-'
                        })()}
                    </div>
                ),
                category: activeError ? (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${categoryStyle}`}>
                        {i18n.t(`fulfillment_errors.category.${activeError.category}`, { defaultValue: activeError.category })}
                    </span>
                ) : '-',
                responsible_area: activeError ? (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${responsibleStyle}`}>
                        {i18n.t(`fulfillment_errors.responsible_area.${activeError.responsible_area}`, { defaultValue: activeError.responsible_area })}
                    </span>
                ) : '-',
                operator_action: activeError?.flag_type ? (
                    <div className="text-sm text-gray-700 max-w-xs">
                        {i18n.t(`fulfillment_errors.operator_action.${activeError.flag_type}`, { defaultValue: '-' })}
                    </div>
                ) : (
                    <div className="text-sm text-gray-400">-</div>
                ),
                error_status: activeError ? (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${ERROR_STATUS_STYLES[activeError.error_status] || 'bg-gray-100 text-gray-800'}`}>
                        {i18n.t(`fulfillment_errors.error_status.${activeError.error_status}`, { defaultValue: activeError.error_status })}
                    </span>
                ) : '-',
                created_at: activeError ? (
                    <FormattedRelativeTime date={activeError.created_at} />
                ) : '-',
            }
        })
    }, [orders, i18n])

    const filtersInitialData = [
        {
            key: "order_number",
            placeholder: "orders.filters.paste_order_numbers",
            label: "fulfillment_errors.page.filters.order_number",
            data: {
                visible: true,
                type: 'bulk',
                value: []
            },
        },
        {
            key: "store",
            label: "fulfillment_errors.page.filters.store",
            data: {
                visible: true,
                type: 'text',
                value: ''
            },
        },
        {
            // The backend receives it as `tote_search` and matches the exact code, not by
            // similarity: the operator arrives with the tote in hand and types it in full.
            key: "tote_search",
            label: "fulfillment_errors.page.filters.tote",
            data: {
                visible: true,
                type: 'text',
                value: '',
                placeholder: "fulfillment_errors.page.filters.tote_placeholder",
            },
        },
        {
            key: "fulfillment_error_responsible_area",
            label: "fulfillment_errors.page.filters.responsible_area",
            data: {
                visible: true,
                type: 'multiFilter',
                value: [],
                placeholder: "fulfillment_errors.page.filters.responsible_area_placeholder",
                options: getResponsibleAreaFilterOptions(i18n)
            },
        },
        {
            key: "fulfillment_error_status",
            label: "fulfillment_errors.page.filters.error_status",
            data: {
                visible: true,
                type: 'multiFilter',
                value: [],
                placeholder: "fulfillment_errors.page.filters.error_status_placeholder",
                options: getErrorStatusFilterOptions(i18n)
            },
        },
    ]

    const [filtersData, setFiltersData] = useState(filtersInitialData)

    const updateFiltersData = (updatedData, filter) => {
        setFiltersData((prev) => {
            return prev.map((item) => {
                if (item.key === filter)
                    return { ...item, data: updatedData }
                else return item
            }).sort((a, b) => a.data.visible > b.data.visible ? -1 : 1)
        })

        const filterItem = filtersData.find(item => item.key === filter)
        if ((Array.isArray(filterItem.data.value) && filterItem.data.value.length === 0 && updatedData.value.length === 0)) return
        if (!['boolean', 'text'].includes(filterItem.data.type) && filterItem.data.value === updatedData.value) return

        const value = updatedData.value
        const filter_name = filterItem.key

        if (!updatedData.visible) {
            setSearchParams((prev) => {
                const newSearchParams = { ...prev, page: 1 }
                delete newSearchParams[filter_name]
                return newSearchParams
            })
        } else {
            setSearchParams((prev) => ({ ...prev, [filter_name]: value, page: 1 }))
        }
    }

    const handleResetFilters = () => {
        setOrders([])
        queryClient.setQueryData(['fulfillment_errors_orders', searchParams], null)
        setSearchParams(initialSearchParams)
    }

    const handleRowClick = (order) => {
        history.push(FULFILLMENT_ERROR_ORDER_DETAIL.replace(":id", order.id).replace(':warehouse_id', warehouse_id))
    }

    return (<>
        <PageView
            topMenu={
                <PageTopBar>
                    <div className="flex items-center gap-2">
                        <ExclamationCircleIcon className="h-6 w-6 text-red-500" />
                        <div className="text-lg font-semibold">{i18n.t('fulfillment_errors.page.title')}</div>
                    </div>
                </PageTopBar>
            }
            childrenFullWidth={true}
            topMenuFullWidth={true}
        >
            {isLoading && !isPreviousData ? (
                <Loader show={true} />
            ) : isError ? (
                <div className="text-center py-12">
                    <p className="text-base text-red-600 mb-4">{error.message}</p>
                    <button
                        type="button"
                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        onClick={() => { setIsFetchEnabled(true); refetch(); }}
                    >
                        {i18n.t('fulfillment_errors.error_modal.retry')}
                    </button>
                </div>
            ) : (
                <NewTable
                    data={memoizedTableData}
                    columns={memoizedColumns}
                    showLoader={isFetching && isPreviousData}
                    showPaginationOnFooter
                    isFetching={isFetching}
                    emptyTableText={i18n.t('fulfillment_errors.page.empty')}
                    paginationMeta={data?.meta ? data.meta : { per_page: 25 }}
                    onPaginationChange={(page) => {
                        setSearchParams((prev) => ({ ...prev, page: page }))
                        setIsFetchEnabled(true)
                    }}
                    onPageSizeChange={(page) => {
                        if (page.id !== searchParams.per_page) {
                            setSearchParams((prev) => ({ ...prev, per_page: page.id, page: 1 }))
                            if (orders?.length > 0) setIsFetchEnabled(true)
                        }
                    }}
                    onSortChange={onSortChange}
                    onSearchHandler={updateFiltersData}
                    handleResetFilters={handleResetFilters}
                    filtersData={filtersData}
                    rowProps={row => ({
                        onClick: () => handleRowClick(row.original?.object || row.values?.object)
                    })}
                    applyFiltersManually={true}
                    onManuallyAppliedFilters={() => setIsFetchEnabled(true)}
                />
            )}
        </PageView>
        <Switch>
            <Route exact path={FULFILLMENT_ERROR_ORDER_DETAIL}>
                <SlidePanel title={i18n.t('fulfillment_errors.page.title')} referrer={FULFILLMENT_ERRORS.replace(':warehouse_id', warehouse_id)}>
                    <ShowOrderContainer showPackingAction={true} />
                </SlidePanel>
            </Route>
        </Switch>
    </>
    )
}
